import { apiFetch, ApiError } from '../apiFetch'

export type StageName =
  | 'PHOI'
  | 'DINH_HINH'
  | 'LAP_RAP'
  | 'CHA_NHAM'
  | 'SON'
  | 'FITTING'
  | 'DONG_GOI'

export const PRODUCTION_STAGES: readonly StageName[] = [
  'PHOI',
  'DINH_HINH',
  'LAP_RAP',
  'CHA_NHAM',
  'SON',
  'FITTING',
  'DONG_GOI',
] as const

export const PART_LEVEL_STAGES: readonly StageName[] = ['PHOI', 'DINH_HINH'] as const

export type StageDefinition = {
  id: StageName
  label: string
  shortLabel: string
  unit: 'chi tiết' | 'sản phẩm'
  isPartLevel: boolean
  stepNumber: number
}

export const STAGE_DEFINITIONS: Record<StageName, StageDefinition> = {
  PHOI: {
    id: 'PHOI',
    label: 'Phôi',
    shortLabel: '1. Phôi',
    unit: 'chi tiết',
    isPartLevel: true,
    stepNumber: 1,
  },
  DINH_HINH: {
    id: 'DINH_HINH',
    label: 'Định hình',
    shortLabel: '2. Định hình',
    unit: 'chi tiết',
    isPartLevel: true,
    stepNumber: 2,
  },
  LAP_RAP: {
    id: 'LAP_RAP',
    label: 'Lắp ráp hoàn chỉnh',
    shortLabel: '3. Lắp ráp',
    unit: 'sản phẩm',
    isPartLevel: false,
    stepNumber: 3,
  },
  CHA_NHAM: {
    id: 'CHA_NHAM',
    label: 'Chà nhám',
    shortLabel: '4. Chà nhám',
    unit: 'sản phẩm',
    isPartLevel: false,
    stepNumber: 4,
  },
  SON: {
    id: 'SON',
    label: 'Sơn',
    shortLabel: '5. Sơn',
    unit: 'sản phẩm',
    isPartLevel: false,
    stepNumber: 5,
  },
  FITTING: {
    id: 'FITTING',
    label: 'Fitting',
    shortLabel: '6. Fitting',
    unit: 'sản phẩm',
    isPartLevel: false,
    stepNumber: 6,
  },
  DONG_GOI: {
    id: 'DONG_GOI',
    label: 'Đóng gói',
    shortLabel: '7. Đóng gói',
    unit: 'sản phẩm',
    isPartLevel: false,
    stepNumber: 7,
  },
}

export type StageWarningCode =
  | 'OVER_PLANNED'
  | 'OVER_PREVIOUS_STAGE'
  | 'OVER_ASSEMBLY_SETS'
  | 'REVERSAL_BELOW_DOWNSTREAM'
  | 'NON_INTEGER_QTY_PER'
  | 'UNDO_WINDOW_EXPIRED'

export type StageWarning = {
  code: StageWarningCode | string
  message: string
  details?: Record<string, unknown>
}

export type StageProgressPart = {
  part_snapshot_id: string
  bom_wood_line_id: string
  part_name: string
  part_group: string | null
  spec: string
  wood_type: string | null
  qty_per: number
  target_qty: number
  cum_phoi: number
  cum_dinh_hinh: number
  wip_available: number
  eligible_sets: number
}

export type BottleneckInfo = {
  part_snapshot_id: string
  bom_wood_line_id: string
  part_name: string
  spec: string
  qty_per: number
  completed_dinh_hinh: number
  used_in_assembly: number
  available_wip: number
  max_sets_possible: number
  deficit_units?: number
}

export type StageSummaryItem = {
  stage: StageName
  completed_qty?: number
  completed_units?: number
  required_units?: number
  max_eligible_qty?: number
  progress_pct: number
  equivalent_sets?: number
}

export type StageProgressResult = {
  plan_line_id: string
  order_no: string | null
  customer_name: string | null
  product_code: string | null
  product_name: string | null
  variant_name: string | null
  planned_qty: number
  stages: Record<StageName, StageSummaryItem>
  bottleneck: BottleneckInfo | null
  parts: StageProgressPart[]
  warnings: StageWarning[]
}

export type ProductionOrderOverviewItem = {
  id: string
  planned_qty: number
  active: boolean
  done: boolean
  order_no: string | null
  customer_name: string | null
  product_code: string | null
  product_name: string | null
  variant_name: string | null
}

export type RecordStageEventItem = {
  id: string
  plan_line_id: string
  stage: StageName
  part_snapshot_id: string | null
  qty: number
  production_date: string
  created_at: string
  undo_deadline?: string
}

export type RecordStageEventsPayload = {
  stage: StageName
  production_date: string
  note?: string | null
  items?: Array<{
    part_snapshot_id?: string | null
    qty: number
  }>
  part_snapshot_id?: string | null
  qty?: number
}

export type RecordStageEventsResponse = {
  events: RecordStageEventItem[]
  event?: RecordStageEventItem
  warnings: StageWarning[]
  undo_deadline: string
  is_retry?: boolean
}

export type ReverseStageEventResponse = {
  event: RecordStageEventItem
  warnings: StageWarning[]
  is_retry?: boolean
}

/**
 * Generates standard UUID v4 for Idempotency-Key
 */
export function generateIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/**
 * Returns today's date in Vietnam timezone (Asia/Ho_Chi_Minh) as YYYY-MM-DD
 */
export function getTodayVietnamDate(): string {
  const now = new Date()
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Ho_Chi_Minh',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
  return formatter.format(now)
}

/**
 * Translates server stage warnings to friendly Vietnamese
 */
export function translateStageWarning(warning: StageWarning): string {
  if (!warning) return ''
  const details = (warning.details || {}) as Record<string, any>
  const stageLabel = details.stage
    ? (STAGE_DEFINITIONS[details.stage as StageName]?.label || details.stage)
    : ''

  switch (warning.code) {
    case 'OVER_PLANNED': {
      if (details.part_name) {
        const excess = details.excess_qty != null ? ` ${details.excess_qty}` : ''
        return `Đã ghi. Lưu ý: vượt kế hoạch chi tiết "${details.part_name}"${excess} chiếc.`
      }
      const excess = details.excess_qty != null ? ` ${details.excess_qty}` : ''
      return `Đã ghi. Lưu ý: vượt kế hoạch công đoạn ${stageLabel}${excess} sản phẩm.`
    }
    case 'OVER_PREVIOUS_STAGE': {
      if (details.part_name) {
        const excess = details.excess_qty != null ? ` ${details.excess_qty}` : ''
        return `Đã ghi. Lưu ý: Định hình chi tiết "${details.part_name}" vượt sản lượng Phôi${excess} chiếc.`
      }
      const excess = details.excess_qty != null ? ` ${details.excess_qty}` : ''
      const prev = details.previous_stage_qty != null ? ` (công đoạn trước có ${details.previous_stage_qty})` : ''
      return `Đã ghi. Lưu ý: vượt công đoạn trước${excess} sản phẩm${prev}.`
    }
    case 'OVER_ASSEMBLY_SETS': {
      const excess = details.excess_qty != null ? ` ${details.excess_qty}` : ''
      const bottleneck = details.bottleneck_part ? ` (nút thắt: ${details.bottleneck_part})` : ''
      return `Đã ghi. Lưu ý: Lắp ráp vượt số bộ chi tiết đủ tối đa${excess} sản phẩm${bottleneck}.`
    }
    case 'REVERSAL_BELOW_DOWNSTREAM': {
      const deficit = details.deficit_qty != null ? ` ${details.deficit_qty}` : ''
      return `Đã ghi. Lưu ý: sau khi hoàn tác, sản lượng ${stageLabel} thấp hơn công đoạn kế tiếp${deficit}.`
    }
    case 'NON_INTEGER_QTY_PER': {
      const partName = details.part_name ? ` "${details.part_name}"` : ''
      return `Lưu ý: Chi tiết${partName} có định mức lẻ, số bộ được tính làm tròn xuống.`
    }
    case 'UNDO_WINDOW_EXPIRED': {
      return 'Lưu ý: Đã hết hạn hoàn tác nhanh 15 phút, giao dịch đã được ghi nhận.'
    }
    default:
      return warning.message || 'Cảnh báo không xác định'
  }
}

/**
 * Translates server API errors to friendly Vietnamese
 */
export function translateStageError(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'BOM_NOT_FOUND':
        return 'Mã hàng này chưa có định mức mộc đang dùng. Hãy tạo/kích hoạt định mức trước.'
      case 'BOM_WOOD_LINES_EMPTY':
        return 'BOM active của sản phẩm này không có chi tiết mộc nào hợp lệ.'
      case 'VARIANT_NOT_FOUND':
        return 'Lệnh sản xuất chưa được gán biến thể sản phẩm.'
      case 'ORDER_NOT_FOUND':
        return 'Không tìm thấy lệnh sản xuất hoặc lệnh đã bị đóng/hủy.'
      case 'READONLY_NO_SNAPSHOT':
        return 'Lệnh chưa có danh sách chi tiết; cần người có quyền ghi mở lệnh một lần.'
      case 'EVENT_ALREADY_REVERSED':
        return 'Lượt ghi nhận này đã được hoàn tác trước đó.'
      case 'NEGATIVE_NET_QTY':
        return 'Không thể hoàn tác vì sẽ làm tổng lũy kế của công đoạn bị âm.'
      case 'IDEMPOTENCY_KEY_REUSED':
        return 'Khóa gửi bị trùng lặp với nội dung khác. Vui lòng thử lại.'
      case 'IDEMPOTENCY_KEY_REQUIRED':
        return 'Thiếu khóa chống gửi đúp (Idempotency-Key).'
      case 'INVALID_QUANTITY':
        return 'Số lượng ghi nhận phải lớn hơn 0.'
      case 'EMPTY_ITEMS':
        return 'Vui lòng nhập số lượng lớn hơn 0 cho ít nhất một dòng.'
      case 'FORBIDDEN':
        return 'Bạn không có quyền thực hiện thao tác này (yêu cầu quyền planning.write).'
      case 'REASON_REQUIRED':
        return 'Vui lòng nhập lý do hoàn tác.'
      default:
        return err.message || 'Đã có lỗi xảy ra phía máy chủ.'
    }
  }
  if (err instanceof Error) {
    return err.message || 'Đã có lỗi xảy ra.'
  }
  return 'Lỗi không xác định.'
}

/**
 * API call to list active production orders for stage recording
 */
export async function fetchActiveProductionOrders(search: string = ''): Promise<ProductionOrderOverviewItem[]> {
  const query = new URLSearchParams()
  query.set('status', 'running')
  query.set('limit', '100')
  if (search.trim()) {
    query.set('search', search.trim())
  }

  const res = await apiFetch(`/api/production-stages/overview?${query.toString()}`)
  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}))
    throw new ApiError(
      errorBody.error?.code || 'FETCH_ORDERS_FAILED',
      errorBody.error?.message || 'Không thể tải danh sách lệnh sản xuất',
      res.status,
      errorBody.error?.details,
    )
  }
  const body = await res.json()
  return body.data || []
}

/**
 * API call to get 7-stage progress and parts snapshot for an order
 */
export async function fetchStageProgress(planLineId: string): Promise<StageProgressResult> {
  const res = await apiFetch(`/api/production-orders/${encodeURIComponent(planLineId)}/stage-progress`)
  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}))
    throw new ApiError(
      errorBody.error?.code || 'FETCH_PROGRESS_FAILED',
      errorBody.error?.message || 'Không thể tải tiến độ lệnh sản xuất',
      res.status,
      errorBody.error?.details,
    )
  }
  const body = await res.json()
  return body.data
}

export type StageSnapshotsResult = {
  plan_line_id: string
  total_parts: number
  snapshotted_at: string
  snapshots: Array<{
    id: string
    plan_line_id: string
    bom_wood_line_id: string
    part_name: string
    part_group: string | null
    sort_order: number | null
    dims_t: number | null
    dims_w: number | null
    dims_l: number | null
    wood_type: string | null
    qty_per: number
    notes: string | null
    snapshotted_by: string
    snapshotted_at: string
  }>
}

/**
 * API call to take/ensure part snapshots from active BOM (Idempotent)
 */
export async function createStageSnapshots(planLineId: string): Promise<StageSnapshotsResult> {
  const res = await apiFetch(`/api/production-orders/${encodeURIComponent(planLineId)}/stage-snapshots`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({}),
  })

  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}))
    throw new ApiError(
      errorBody.error?.code || 'SNAPSHOT_FAILED',
      errorBody.error?.message || 'Không thể nạp danh sách chi tiết từ định mức',
      res.status,
      errorBody.error?.details,
    )
  }

  const body = await res.json()
  return body.data
}

/**
 * API call to record stage events with Idempotency-Key
 */
export async function recordStageEvents(
  planLineId: string,
  payload: RecordStageEventsPayload,
  idempotencyKey: string,
): Promise<RecordStageEventsResponse> {
  const res = await apiFetch(`/api/production-orders/${encodeURIComponent(planLineId)}/stage-events`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Idempotency-Key': idempotencyKey,
    },
    body: JSON.stringify(payload),
  })

  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}))
    throw new ApiError(
      errorBody.error?.code || 'RECORD_FAILED',
      errorBody.error?.message || 'Không thể ghi nhận sản lượng',
      res.status,
      errorBody.error?.details,
    )
  }

  const body = await res.json()
  return body.data
}

/**
 * API call to reverse an event with Idempotency-Key and reason
 */
export async function reverseStageEvent(
  planLineId: string,
  eventId: string,
  reason: string = 'Hoàn tác nhanh',
  idempotencyKey: string,
): Promise<ReverseStageEventResponse> {
  const res = await apiFetch(
    `/api/production-orders/${encodeURIComponent(planLineId)}/stage-events/${encodeURIComponent(eventId)}/reverse`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Idempotency-Key': idempotencyKey,
      },
      body: JSON.stringify({ reason }),
    },
  )

  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}))
    throw new ApiError(
      errorBody.error?.code || 'REVERSE_FAILED',
      errorBody.error?.message || 'Không thể hoàn tác sự kiện',
      res.status,
      errorBody.error?.details,
    )
  }

  const body = await res.json()
  return body.data
}

export type WorkshopOverviewItem = {
  id: string
  plan_line_id: string
  order_no: string | null
  customer_name: string | null
  product_code: string | null
  product_name: string | null
  variant_name: string | null
  planned_qty: number
  active: boolean
  done: boolean
  stages: Record<StageName, StageSummaryItem>
  bottleneck: BottleneckInfo | null
  warnings_count: number
}

export type WorkshopOverviewResponse = {
  items: WorkshopOverviewItem[]
  pagination: {
    page: number
    limit: number
    total: number
    total_pages: number
  }
}

/**
 * Tải bảng tiến độ tổng quan toàn xưởng (hỗ trợ phân trang, tìm kiếm, lọc trạng thái)
 */
export async function fetchWorkshopOverview(params: {
  page?: number
  limit?: number
  search?: string
  status?: 'running' | 'completed' | 'all'
} = {}): Promise<WorkshopOverviewResponse> {
  const query = new URLSearchParams()
  if (params.page) query.set('page', String(params.page))
  if (params.limit) query.set('limit', String(params.limit))
  if (params.search?.trim()) query.set('search', params.search.trim())
  if (params.status) query.set('status', params.status)

  const res = await apiFetch(`/api/production-stages/overview?${query.toString()}`)
  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}))
    throw new ApiError(
      errorBody.error?.code || 'FETCH_OVERVIEW_FAILED',
      errorBody.error?.message || 'Không thể tải bảng tiến độ toàn xưởng',
      res.status,
      errorBody.error?.details,
    )
  }
  const body = await res.json()
  const rawItems = body.data || []
  const items: WorkshopOverviewItem[] = rawItems.map((item: any) => ({
    ...item,
    id: item.id || item.plan_line_id,
    plan_line_id: item.plan_line_id || item.id,
  }))
  return {
    items,
    pagination: body.pagination || {
      page: params.page || 1,
      limit: params.limit || 30,
      total: items.length,
      total_pages: 1,
    },
  }
}

export type StageEventListItem = {
  id: string
  plan_line_id: string
  stage: StageName
  part_snapshot_id: string | null
  part_name: string | null
  spec: string
  wood_type: string | null
  qty_per: number | null
  qty: number
  production_date: string
  idempotency_key: string
  reverses_event_id: string | null
  is_reversal: boolean
  note: string | null
  actor: string
  created_at: string
  reversed_by_event_id: string | null
  reversed_at: string | null
  can_reverse: boolean
}

export type StageEventsResponse = {
  events: StageEventListItem[]
  pagination: {
    page: number
    limit: number
    total: number
    total_pages: number
  }
}

/**
 * Tải danh sách sự kiện sản xuất theo lệnh (phân trang, chi tiết và trạng thái đảo)
 */
export async function fetchOrderStageEvents(
  planLineId: string,
  params: { page?: number; limit?: number; stage?: StageName } = {},
): Promise<StageEventsResponse> {
  const query = new URLSearchParams()
  if (params.page) query.set('page', String(params.page))
  if (params.limit) query.set('limit', String(params.limit))
  if (params.stage) query.set('stage', params.stage)

  const res = await apiFetch(
    `/api/production-orders/${encodeURIComponent(planLineId)}/stage-events?${query.toString()}`,
  )
  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}))
    throw new ApiError(
      errorBody.error?.code || 'FETCH_EVENTS_FAILED',
      errorBody.error?.message || 'Không thể tải lịch sử sự kiện sản xuất',
      res.status,
      errorBody.error?.details,
    )
  }
  const body = await res.json()
  return {
    events: body.data || [],
    pagination: body.pagination || {
      page: params.page || 1,
      limit: params.limit || 20,
      total: (body.data || []).length,
      total_pages: 1,
    },
  }
}
